import { AppHeader } from '@/components/layout/AppHeader';
import { SystemIntegrityIndicator } from '@/components/dashboard/SystemIntegrityIndicator';
import { Link, useNavigate } from 'react-router-dom';
import { FileText, Download, ArrowRight, ClipboardList, User } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useUserRole } from '@/hooks/useUserRole';
import { useComplianceStats } from '@/hooks/useComplianceStats';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

function useCountUp(target: number, shouldStart: boolean): number {
  const [current, setCurrent] = useState(0);
  const hasFired = useRef(false);

  useEffect(() => {
    if (!shouldStart || hasFired.current || target === 0) return;
    hasFired.current = true;

    const duration = 700;
    let start: number | null = null;
    const step = (timestamp: number) => {
      if (!start) start = timestamp;
      const elapsed = timestamp - start;
      const t = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3); // easeOut
      setCurrent(Math.round(eased * target));
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [shouldStart, target]);

  return current;
}

interface StatusTileProps {
  count: number;
  label: string;
  mobileLabel?: string;
  allCaps?: boolean;
  activeClass: string;
  textClass: string;
  onClick: () => void;
}

function StatusTile({ count, label, mobileLabel, allCaps, activeClass, textClass, onClick }: StatusTileProps) {
  const isActive = count > 0;
  const displayLabel = label;
  return (
    <div
      className={cn("tile px-3 md:p-6 flex flex-col justify-between !items-start !text-left overflow-hidden min-w-0 min-h-[160px] md:min-h-auto py-[4px] pt-[12px] pb-[12px]", isActive ? activeClass : 'tile-clear')}
      onClick={onClick}>

      <span
        className={cn("text-[18px] text-white leading-[1.1] break-words font-extrabold tracking-[-0.03em]",

        isActive && allCaps && 'uppercase'
        )}>

        {mobileLabel ?
        <>
            <span className="lg:hidden">{mobileLabel}</span>
            <span className="hidden lg:inline">{label}</span>
          </> :
        displayLabel}
      </span>
      <div className="w-full mt-auto flex items-end justify-between">
        {isActive ?
        <span className="text-[56px] md:text-[64px] font-black text-white leading-none translate-y-[10px] md:translate-y-[6px]">
            {count}
          </span> :

        <span className="text-[56px] md:text-[64px] font-black text-white leading-none translate-y-[10px] md:translate-y-[6px]">0</span>
        }
        {isActive && <ArrowRight className="h-4 w-4 text-white/50 translate-y-[4px] md:mb-2" />}
      </div>
    </div>);

}

interface FlipTileProps extends StatusTileProps {
  delay: number;
}

function FlipTile({ delay, count, ...rest }: FlipTileProps) {
  const [countUpStarted, setCountUpStarted] = useState(false);
  const animatedCount = useCountUp(count, countUpStarted);

  useEffect(() => {
    if (count > 0) {
      const timer = setTimeout(() => setCountUpStarted(true), (delay + 0.5) * 1000);
      return () => clearTimeout(timer);
    }
  }, [count, delay]);

  if (count === 0) {
    return (
      <motion.div
        className="h-[160px] md:h-[120px]"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut', delay }}
      >
        <StatusTile count={0} {...rest} />
      </motion.div>
    );
  }

  return (
    <div className="h-[160px] md:h-[120px]" style={{ perspective: 800, perspectiveOrigin: '50% 50%' }}>
      <motion.div
        style={{ transformStyle: 'preserve-3d', width: '100%', height: '100%', position: 'relative' }}
        initial={{ rotateX: 0 }}
        animate={{ rotateX: 180 }}
        transition={{ duration: 0.4, ease: 'easeInOut', delay: delay + 0.2 }}
      >
        {/* Front face — inactive/blue */}
        <div style={{ backfaceVisibility: 'hidden', position: 'absolute', inset: 0 }}>
          <StatusTile count={0} {...rest} />
        </div>
        {/* Back face — active colored */}
        <div style={{ backfaceVisibility: 'hidden', position: 'absolute', inset: 0, transform: 'rotateX(180deg)' }}>
          <StatusTile count={animatedCount} {...rest} />
        </div>
      </motion.div>
    </div>
  );
}

export default function Index() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { data: userRole } = useUserRole();
  const isMobile = useIsMobile();
  const { data: stats } = useComplianceStats(
    profile?.id ?? '',
    userRole ?? ''
  );

  const isAdminOrOwner = userRole === 'admin';

  return (
    <div className="min-h-screen bg-background pb-14 md:pb-0">
      <AppHeader />

      <main className="container px-4 pt-10 pb-8">
        <h1
          className="nav-card-title mb-0.5 text-[32px] font-black tracking-tight leading-none"
          style={{ color: 'hsl(var(--header-from))' }}>

          Dashboard
        </h1>
        <p className="text-[13px] text-muted-foreground mb-4">
          Track, manage and store your policies
        </p>

        <div className="mb-4">
          <SystemIntegrityIndicator />
        </div>

        {/* Status Tiles - 4 across on desktop, 2x2 on mobile */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-2.5 mb-6">
          <FlipTile
            delay={0}
            count={stats?.overdueCount ?? 0}
            label="Over Due"
            allCaps
            activeClass="tile-overdue"
            textClass="text-tile-overdue-text"
            onClick={() => navigate('/policies?status=overdue')} />

          <FlipTile
            delay={0.2}
            count={stats?.dueSoonCount ?? 0}
            label="Due soon"
            activeClass="tile-duesoon"
            textClass="text-tile-duesoon-text"
            onClick={() => navigate('/policies?status=due_soon')} />

          {isAdminOrOwner &&
          <FlipTile
            delay={0.1}
            count={stats?.pendingApprovalCount ?? 0}
            label="Pending approval"
            mobileLabel="Pending"
            activeClass="tile-pending"
            textClass="text-tile-pending-text"
            onClick={() => navigate('/policies?status=pending_approval')} />
          }

          {isAdminOrOwner &&
          <FlipTile
            delay={0.4}
            count={stats?.draftsInProgressCount ?? 0}
            label="Drafts in progress"
            mobileLabel="Drafts"
            activeClass="tile-draft"
            textClass="text-primary"
            onClick={() => navigate('/policies?composerMode=draft')} />
          }

          <FlipTile
            delay={0.3}
            count={stats?.needAcknowledgmentPolicies ?? 0}
            label="Need acknowledgement"
            mobileLabel="Need ack"
            activeClass="tile-ack"
            textClass="text-primary"
            onClick={() => navigate('/policies?status=need_ack')} />
        </div>

        {/* Nav Cards - 2 cols desktop, 1 col mobile */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <Link to="/policies?view=mine">
            <div className="nav-card-gradient rounded-[10px] p-[22px] min-h-[160px] flex flex-col justify-between relative overflow-hidden cursor-pointer">
              <div>
                <h2
                  className="nav-card-title text-[24px] font-black mb-1 tracking-[-0.03em]"
                  style={{ color: 'hsl(var(--header-from))' }}>

                  My Policies
                </h2>
                <p className="nav-card-subtitle text-[12px] text-muted-foreground">View and manage my policies</p>
              </div>
              <User className="absolute bottom-[8px] right-[8px] h-[150px] w-[150px] text-primary opacity-[0.10] pointer-events-none" />
              <div className="flex items-center justify-between mt-4">
                <span className="nav-card-link text-[12px] font-semibold tracking-[0.02em]" style={{ color: 'hsl(var(--header-from))' }}>
                  Browse my policies
                </span>
                <ArrowRight className="h-5 w-5 text-primary" />
              </div>
            </div>
          </Link>

          <Link to="/policies">
            <div className="nav-card-gradient rounded-[10px] p-[22px] min-h-[160px] flex flex-col justify-between relative overflow-hidden cursor-pointer">
              <div>
                <h2
                  className="nav-card-title text-[24px] font-black mb-1 tracking-[-0.03em]"
                  style={{ color: 'hsl(var(--header-from))' }}>

                  Policies
                </h2>
                <p className="nav-card-subtitle text-[12px] text-muted-foreground">View and manage clinic policies</p>
              </div>
              <FileText className="absolute bottom-[8px] right-[8px] h-[140px] w-[140px] text-primary opacity-[0.10] pointer-events-none" />
              <div className="flex items-center justify-between mt-4">
                <span className="nav-card-link text-[12px] font-semibold tracking-[0.02em]" style={{ color: 'hsl(var(--header-from))' }}>
                  Browse all policies
                </span>
                <ArrowRight className="h-5 w-5 text-primary" />
              </div>
            </div>
          </Link>
        </div>

        {/* Audit Export - full width, hidden on mobile */}
        {!isMobile &&
        <Link to="/audit-export">
            <div className="nav-card-gradient rounded-[10px] p-[22px] min-h-[120px] flex flex-col justify-between relative overflow-hidden cursor-pointer">
              <div>
                <h2
                className="nav-card-title text-[24px] font-black mb-1 tracking-[-0.03em]"
                style={{ color: 'hsl(var(--header-from))' }}>

                  Audit Export
                </h2>
                <p className="nav-card-subtitle text-[12px] text-muted-foreground">Export compliance reports</p>
              </div>
              <Download className="absolute bottom-0 right-[8px] h-[140px] w-[140px] text-primary opacity-[0.10] pointer-events-none top-1/2 -translate-y-1/2" />
              <div className="flex items-center justify-between mt-4">
                <span className="nav-card-link text-[12px] font-semibold tracking-[0.02em]" style={{ color: 'hsl(var(--header-from))' }}>
                  Generate CSV or PDF reports
                </span>
                <ArrowRight className="h-5 w-5 text-primary" />
              </div>
            </div>
          </Link>
        }
      </main>
    </div>);

}